/**
 * WeaveIn.ai Platform - Step Type Picker Component
 * Visual step type selection with categorized options and clear labels
 */

import React, { useState } from 'react';
import {
  MessageSquare,
  Mail,
  Phone,
  Bot,
  FileText,
  Upload,
  ClipboardCheck,
  Clock,
  MessageCircle,
  Inbox,
  CheckSquare,
  ThumbsUp,
  Plug,
  DollarSign,
  Bell,
  GitBranch,
  ChevronDown,
  ChevronRight,
  Search,
  BrainCircuit,
  TrendingUp,
  Lightbulb,
  Stethoscope,
  HeartPulse,
  Radio,
  UserCheck,
  ShieldCheck,
  Hash,
  BellRing,
} from 'lucide-react';
import { StepType } from '@/services/api/workflows';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { defaultNotifyConfig } from './notify/notifySteps';

export interface StepTypeOption {
  value: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  baseType: StepType;
  defaultConfig?: Record<string, any>;
  /** i18n keys for label / description (the literals are the fallback). */
  labelKey?: string;
  descriptionKey?: string;
}

export interface StepTypeCategory {
  id: string;
  label: string;
  labelKey?: string;
  icon: React.ReactNode;
  types: StepTypeOption[];
}

// Define all step types with clear labels and categories
export const STEP_TYPE_CATEGORIES: StepTypeCategory[] = [
  {
    id: 'clinical_social',
    label: 'Clinical → Social',
    icon: <Stethoscope className="w-4 h-4" />,
    types: [
      {
        value: 'clinical_signal',
        label: 'Clinical Signal',
        icon: <HeartPulse className="w-5 h-5 text-rose-600" />,
        description: 'Entry gate: health insight, EHR diagnosis, or observation threshold that initiated the loop',
        baseType: 'notification',
        defaultConfig: { sources: ['approved_insight', 'icd_codes', 'observation_threshold'] },
      },
      {
        value: 'human_approval',
        label: 'Clinician Approval Gate',
        icon: <ShieldCheck className="w-5 h-5 text-amber-600" />,
        description: 'Pause for a clinician/coordinator to approve or reject before the workflow proceeds',
        baseType: 'approval',
        defaultConfig: { role: 'clinician', on_reject: 'end' },
      },
      {
        value: 'create_referral',
        label: 'Create Social Referral(s)',
        icon: <Stethoscope className="w-5 h-5 text-indigo-600" />,
        description: 'Create one or more social referrals from the clinical signal via the ICD→SDOH mapping',
        baseType: 'task',
        defaultConfig: { source: 'icd_to_sdoh_mapping', multi: true },
      },
      {
        value: 'multi_channel_escalation',
        label: 'Multi-Channel Outreach',
        icon: <Radio className="w-5 h-5 text-cyan-600" />,
        description: 'Reach the member across SMS, voice AI and email with escalation between channels',
        baseType: 'communication',
        defaultConfig: { channels: ['sms', 'voice', 'email'], escalate_after_hours: 24 },
      },
      {
        value: 'notify_pcp',
        label: 'Notify PCP (Loop-back)',
        icon: <UserCheck className="w-5 h-5 text-green-600" />,
        description: 'Close the loop back to the clinician: flag PCP notification + share the outcome',
        baseType: 'notification',
        defaultConfig: { channel: 'portal' },
      },
      {
        value: 'ai_agent',
        label: 'AI Agent Step',
        icon: <Bot className="w-5 h-5 text-violet-600" />,
        description: 'Invoke an authored LLM agent for a grounded decision — advises, or halts the workflow on decline',
        baseType: 'condition',
        defaultConfig: { agent_key: '', decline_halts: true },
      },
    ],
  },
  {
    id: 'communication',
    label: 'Communication',
    icon: <MessageSquare className="w-4 h-4" />,
    types: [
      {
        value: 'communication_sms',
        label: 'SMS Message',
        icon: <MessageSquare className="w-5 h-5 text-cyan-600" />,
        description: 'Send text message to member or contact',
        baseType: 'communication',
        defaultConfig: { channel: 'sms' },
      },
      {
        value: 'communication_email',
        label: 'Email',
        icon: <Mail className="w-5 h-5 text-blue-600" />,
        description: 'Send email to member or contact',
        baseType: 'communication',
        defaultConfig: { channel: 'email' },
      },
      {
        value: 'communication_phone',
        label: 'Phone Call (Manual)',
        icon: <Phone className="w-5 h-5 text-green-600" />,
        description: 'Staff makes outbound call',
        baseType: 'communication',
        defaultConfig: { channel: 'phone' },
      },
      {
        value: 'communication_voice',
        label: 'Voice Call (AI)',
        icon: <Bot className="w-5 h-5 text-purple-600" />,
        description: 'AI-powered voice outreach',
        baseType: 'communication',
        defaultConfig: { channel: 'voice' },
      },
    ],
  },
  {
    id: 'forms_documents',
    label: 'Forms & Documents',
    icon: <FileText className="w-4 h-4" />,
    types: [
      {
        value: 'form',
        label: 'Complete Form',
        icon: <FileText className="w-5 h-5 text-indigo-600" />,
        description: 'Assign form from library',
        baseType: 'form',
        defaultConfig: { blocks_workflow: true },
      },
      {
        value: 'document',
        label: 'Upload Document',
        icon: <Upload className="w-5 h-5 text-pink-600" />,
        description: 'Request document upload',
        baseType: 'document',
        defaultConfig: { task_type: 'document_upload' },
      },
      {
        value: 'assessment',
        label: 'Assessment',
        icon: <ClipboardCheck className="w-5 h-5 text-amber-600" />,
        description: 'Health assessment (PHQ-9, GAD-7, etc.)',
        baseType: 'form',
        defaultConfig: { form_type: 'assessment' },
      },
    ],
  },
  {
    id: 'wait_response',
    label: 'Wait & Response',
    icon: <Clock className="w-4 h-4" />,
    types: [
      {
        value: 'wait',
        label: 'Wait Period',
        icon: <Clock className="w-5 h-5 text-gray-600" />,
        description: 'Delay before next step',
        baseType: 'wait',
        defaultConfig: { duration_hours: 24 },
      },
      {
        value: 'wait_for_response',
        label: 'Wait for Response',
        icon: <MessageCircle className="w-5 h-5 text-amber-600" />,
        description: 'Wait for member to respond',
        baseType: 'wait_for_response',
        defaultConfig: { timeout_hours: 24, timeout_action: 'escalate' },
      },
      {
        value: 'campaign_response',
        label: 'Campaign Response',
        icon: <Inbox className="w-5 h-5 text-teal-600" />,
        description: 'Wait for campaign reply',
        baseType: 'wait_for_response',
        defaultConfig: { response_type: 'campaign' },
      },
    ],
  },
  {
    id: 'tasks_approvals',
    label: 'Tasks & Approvals',
    icon: <CheckSquare className="w-4 h-4" />,
    types: [
      {
        value: 'task',
        label: 'Manual Task',
        icon: <CheckSquare className="w-5 h-5 text-blue-600" />,
        description: 'Staff action required',
        baseType: 'task',
        defaultConfig: {},
      },
      {
        value: 'approval',
        label: 'Approval Required',
        icon: <ThumbsUp className="w-5 h-5 text-green-600" />,
        description: 'Supervisor/PCP approval',
        baseType: 'approval',
        defaultConfig: { approver_role: 'supervisor' },
      },
    ],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: <Plug className="w-4 h-4" />,
    types: [
      {
        value: 'external_data_ingest',
        label: 'External Data Ingest',
        icon: <Inbox className="w-5 h-5 text-violet-600" />,
        description: 'Receive data from integration workbench (MIF files, MCP data, external systems)',
        baseType: 'external_data_ingest',
        defaultConfig: { source_type: 'integration_workbench', wait_for_data: true },
      },
      {
        value: 'api_call',
        label: 'API Integration',
        icon: <Plug className="w-5 h-5 text-orange-600" />,
        description: 'External system call',
        baseType: 'api_call',
        defaultConfig: {},
      },
      {
        value: 'billing',
        label: 'Billing Entry',
        icon: <DollarSign className="w-5 h-5 text-emerald-600" />,
        description: 'Create billing record',
        baseType: 'billing',
        defaultConfig: { billing_type: 'service', save_as_draft: true },
      },
      {
        value: 'navigate_contact',
        label: 'Navigate to Contact',
        icon: <Search className="w-5 h-5 text-blue-600" />,
        description: 'Open contact detail page or specific tab (overview, activities, documents, conversations)',
        baseType: 'navigate_contact',
        defaultConfig: { target_tab: 'overview' },
      },
    ],
  },
  {
    // TRUS-686: the two steps that tell people something, replacing the old
    // "System Notification" (step type `notification`), which no engine
    // handler ever acted on. Existing steps of that type still resolve through
    // LEGACY_STEP_TYPES below, so they keep their label when opened.
    id: 'notify',
    label: 'Notify',
    labelKey: 'workflows.notify.category',
    icon: <Bell className="w-4 h-4" />,
    types: [
      {
        value: 'notify_channel',
        label: 'Post to channel',
        labelKey: 'workflows.notify.postToChannel',
        icon: <Hash className="w-5 h-5 text-indigo-600" />,
        description: 'WeaveIn posts a message to an Inbox channel, then the workflow moves on',
        descriptionKey: 'workflows.notify.postToChannelDesc',
        baseType: 'notify_channel',
        defaultConfig: defaultNotifyConfig('notify_channel'),
      },
      {
        value: 'notify_staff',
        label: 'Notify staff',
        labelKey: 'workflows.notify.notifyStaff',
        icon: <BellRing className="w-5 h-5 text-amber-600" />,
        description: 'Bell and push notification to the assignee, case manager, roles or people',
        descriptionKey: 'workflows.notify.notifyStaffDesc',
        baseType: 'notify_staff',
        defaultConfig: defaultNotifyConfig('notify_staff'),
      },
    ],
  },
  {
    id: 'ai_powered',
    label: 'AI-Powered',
    icon: <Bot className="w-4 h-4" />,
    types: [
      {
        value: 'provider_match',
        label: 'Provider Match (AI)',
        icon: <Search className="w-5 h-5 text-teal-600" />,
        description: 'AI finds, ranks and suggests providers for the service — auto, or surfaced for staff review',
        baseType: 'condition',
        defaultConfig: { mode: 'auto', max_providers: 5, prefer_network: true },
      },
      {
        value: 'pricing_agent',
        label: 'Pricing Negotiation',
        icon: <Bot className="w-5 h-5 text-violet-600" />,
        description: 'AI negotiates price with providers (pair with an approval gate to review before booking)',
        baseType: 'pricing_agent',
        defaultConfig: { strategy: 'balanced', max_rounds: 5 },
      },
      {
        value: 'condition',
        label: 'Smart Branching',
        icon: <GitBranch className="w-5 h-5 text-purple-600" />,
        description: 'Rule-based or AI-powered routing decision',
        baseType: 'condition',
        defaultConfig: { branching_mode: 'rule_based' },
      },
    ],
  },
  {
    id: 'ml_intelligence',
    label: 'ML Intelligence',
    icon: <BrainCircuit className="w-4 h-4" />,
    types: [
      {
        value: 'prediction_gate',
        label: 'Prediction Gate',
        icon: <TrendingUp className="w-5 h-5 text-rose-600" />,
        description: 'Route based on ML churn prediction score',
        baseType: 'condition',
        defaultConfig: {
          prediction_type: 'churn',
          horizon: '30d',
          thresholds: [
            { condition: 'score >= 0.7', route_to: 'high_risk_path' },
            { condition: 'score >= 0.4', route_to: 'moderate_risk_path' },
            { condition: 'default', route_to: 'low_risk_path' },
          ],
          store_result: true,
        },
      },
      {
        value: 'recommendation_action',
        label: 'Recommendation Action',
        icon: <Lightbulb className="w-5 h-5 text-amber-600" />,
        description: 'Auto-suggest or enroll in recommended programs',
        baseType: 'condition',
        defaultConfig: {
          top_n: 3,
          min_confidence: 0.6,
          action: 'present_to_case_manager',
          auto_enroll_threshold: 0.85,
          filter_reasons: [],
        },
      },
    ],
  },
];

// Flatten all types for lookup
export const ALL_STEP_TYPES: StepTypeOption[] = STEP_TYPE_CATEGORIES.flatMap(
  (cat) => cat.types
);

/**
 * Step types no longer offered in the palette but still found on saved
 * templates. Looked up by value so an old step keeps its label.
 */
export const LEGACY_STEP_TYPES: StepTypeOption[] = [
  {
    value: 'notification',
    label: 'System Notification',
    icon: <Bell className="w-5 h-5 text-yellow-600" />,
    description: 'Internal alert/notification (replaced by Notify staff and Post to channel)',
    baseType: 'notification',
    defaultConfig: {},
  },
];

// Get step type option by value
export const getStepTypeOption = (value: string): StepTypeOption | undefined => {
  return ALL_STEP_TYPES.find((t) => t.value === value) ?? LEGACY_STEP_TYPES.find((t) => t.value === value);
};

type TFn = (key: string, fallback?: string, opts?: Record<string, unknown>) => string;

/** A step type's label / description / category name, translated when it has a key. */
export const stepTypeLabel = (t: TFn, o: StepTypeOption): string => (o.labelKey ? t(o.labelKey, o.label) : o.label);
export const stepTypeDescription = (t: TFn, o: StepTypeOption): string =>
  o.descriptionKey ? t(o.descriptionKey, o.description) : o.description;
export const stepCategoryLabel = (t: TFn, c: StepTypeCategory): string => (c.labelKey ? t(c.labelKey, c.label) : c.label);

// Get step type option by base type and config
export const getStepTypeFromConfig = (
  baseType: StepType,
  config?: Record<string, any>
): StepTypeOption | undefined => {
  // Try to find specific variant first
  if (baseType === 'communication' && config?.channel) {
    const variant = ALL_STEP_TYPES.find(
      (t) => t.baseType === 'communication' && t.defaultConfig?.channel === config.channel
    );
    if (variant) return variant;
  }

  // Fall back to base type
  return ALL_STEP_TYPES.find((t) => t.baseType === baseType) ?? LEGACY_STEP_TYPES.find((t) => t.baseType === baseType);
};

interface StepTypePickerProps {
  selectedType: string;
  onSelect: (value: string, baseType: StepType, defaultConfig: Record<string, any>) => void;
  variant?: 'dropdown' | 'grid' | 'compact';
  className?: string;
}

export const StepTypePicker: React.FC<StepTypePickerProps> = ({
  selectedType,
  onSelect,
  variant = 'dropdown',
  className = '',
}) => {
  const { t } = useAppTranslation();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(STEP_TYPE_CATEGORIES.map((c) => c.id))
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleSelect = (option: StepTypeOption) => {
    onSelect(option.value, option.baseType, option.defaultConfig || {});
    setIsOpen(false);
    setSearchQuery('');
  };

  const selectedOption = getStepTypeOption(selectedType);

  // Filter categories and types based on search
  const filteredCategories = STEP_TYPE_CATEGORIES.map((category) => ({
    ...category,
    types: category.types.filter(
      (type) =>
        stepTypeLabel(t, type).toLowerCase().includes(searchQuery.toLowerCase()) ||
        stepTypeDescription(t, type).toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((category) => category.types.length > 0);

  if (variant === 'grid') {
    return (
      <div className={`step-type-picker-grid ${className}`}>
        <div className="mb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('workflows.stepTypePicker.searchStepTypes', 'Search step types...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="space-y-4 max-h-96 overflow-y-auto">
          {filteredCategories.map((category) => (
            <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleCategory(category.id)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {category.icon}
                  <span className="font-medium text-gray-700">{stepCategoryLabel(t, category)}</span>
                  <span className="text-xs text-gray-500">({category.types.length})</span>
                </div>
                {expandedCategories.has(category.id) ? (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                )}
              </button>

              {expandedCategories.has(category.id) && (
                <div className="p-2 grid grid-cols-2 gap-2">
                  {category.types.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleSelect(type)}
                      className={`flex items-start gap-3 p-3 rounded-lg border transition-all text-left
                        ${
                          selectedType === type.value
                            ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }
                      `}
                    >
                      <div className="flex-shrink-0 mt-0.5">{type.icon}</div>
                      <div className="min-w-0">
                        <div className="font-medium text-sm text-gray-900">{stepTypeLabel(t, type)}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{stepTypeDescription(t, type)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`step-type-picker-compact ${className}`}>
        <select
          value={selectedType}
          onChange={(e) => {
            const option = getStepTypeOption(e.target.value);
            if (option) {
              handleSelect(option);
            }
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {selectedOption && LEGACY_STEP_TYPES.includes(selectedOption) && (
            <option value={selectedOption.value}>{stepTypeLabel(t, selectedOption)}</option>
          )}
          {STEP_TYPE_CATEGORIES.map((category) => (
            <optgroup key={category.id} label={stepCategoryLabel(t, category)}>
              {category.types.map((type) => (
                <option key={type.value} value={type.value}>
                  {stepTypeLabel(t, type)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
    );
  }

  // Default: dropdown variant
  return (
    <div className={`step-type-picker-dropdown relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors"
      >
        {selectedOption ? (
          <div className="flex items-center gap-3">
            {selectedOption.icon}
            <div className="text-left">
              <div className="font-medium text-gray-900">{stepTypeLabel(t, selectedOption)}</div>
              <div className="text-xs text-gray-500">{stepTypeDescription(t, selectedOption)}</div>
            </div>
          </div>
        ) : (
          <span className="text-gray-400">{t('workflows.stepTypePicker.selectStepType', 'Select step type...')}</span>
        )}
        <ChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setIsOpen(false);
              setSearchQuery('');
            }}
          />

          {/* Dropdown panel */}
          <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden">
            {/* Search */}
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('workflows.stepTypePicker.searchStepTypes', 'Search step types...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
            </div>

            {/* Categories and types */}
            <div className="overflow-y-auto max-h-72">
              {filteredCategories.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  {t('workflows.stepTypePicker.noMatchingStepTypes', 'No step types match your search')}
                </div>
              ) : (
                filteredCategories.map((category) => (
                  <div key={category.id}>
                    <div className="px-3 py-2 bg-gray-50 flex items-center gap-2 text-xs font-medium text-gray-600 uppercase tracking-wide">
                      {category.icon}
                      {stepCategoryLabel(t, category)}
                    </div>
                    <div className="py-1">
                      {category.types.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => handleSelect(type)}
                          className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-blue-50 transition-colors text-left
                            ${selectedType === type.value ? 'bg-blue-50' : ''}
                          `}
                        >
                          {type.icon}
                          <div>
                            <div className="font-medium text-sm text-gray-900">{stepTypeLabel(t, type)}</div>
                            <div className="text-xs text-gray-500">{stepTypeDescription(t, type)}</div>
                          </div>
                          {selectedType === type.value && (
                            <div className="ml-auto text-blue-600">✓</div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StepTypePicker;
